// src/pages/payments/NCBATransactionsPage.tsx

import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Eye,
  Filter,
  Loader2,
  PencilLine,
  ReceiptText,
  RefreshCw,
  Search,
  Send,
  ShieldAlert,
  Smartphone,
  Wallet,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import { useToast } from "../../../components/ui/Toast";
import { fetchApp } from "../../../services/apiService";

interface NCBATransaction {
  id: string;
  trans_id: string;
  ft_ref?: string | null;
  trans_type?: string | null;
  trans_time?: string | null;
  trans_amount: number;
  business_short_code: string;
  bill_ref_number?: string | null;
  narrative?: string | null;
  mobile?: string | null;
  customer_name?: string | null;
  username?: string | null;
  payment_id?: string | null;
  processed: boolean;
  process_error?: string | null;
  status: "processed" | "failed" | "unprocessed" | string;
  raw_request?: unknown;
  created_at?: string | null;
  updated_at?: string | null;
}

interface NCBAStats {
  total: number;
  processed: number;
  failed: number;
  unprocessed: number;
  processed_total: number;
}

interface NCBATransactionsResponse {
  ok: boolean;
  page: number;
  page_size: number;
  total: number;
  stats: NCBAStats;
  items: NCBATransaction[];
  message?: string;
}

type StatusFilter = "all" | "processed" | "failed" | "unprocessed";

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13px] font-medium text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCurrencyFromCents = (value?: number | null) => {
  if (value === null || value === undefined) return "KES 0.00";

  return `KES ${(Number(value) / 100).toLocaleString("en-KE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatStatus = (status?: string | null) =>
  String(status || "unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getStatusTone = (status?: string | null) => {
  const value = String(status || "").toLowerCase();

  if (value === "processed") {
    return "border-emerald-200 bg-emerald-100 text-emerald-800";
  }

  if (value === "failed") {
    return "border-rose-200 bg-rose-100 text-rose-700";
  }

  if (value === "unprocessed") {
    return "border-amber-200 bg-amber-100 text-amber-800";
  }

  return "border-slate-300 bg-slate-200 text-slate-700";
};

const StatusBadge: React.FC<{ status?: string | null }> = ({ status }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-black ${getStatusTone(
      status,
    )}`}
  >
    <span className="h-1.5 w-1.5 rounded-full bg-current" />
    {formatStatus(status)}
  </span>
);

const LoadingState: React.FC = () => (
  <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
    <div className="mx-auto flex min-h-[60vh] max-w-[1600px] items-center justify-center">
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
        <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

        <div className="relative overflow-hidden px-6 py-10 text-center">
          <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/50">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>

          <p className="relative mt-4 text-[15px] font-black text-slate-900">
            Loading NCBA transactions…
          </p>

          <p className="relative mt-1 text-[12.5px] font-medium text-slate-500">
            Preparing payment records, stats and correction tools.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber" | "rose";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
    rose: {
      card: "border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 shadow-rose-200/50",
      icon: "bg-rose-100 text-rose-700",
      text: "text-rose-700",
      bubble: "bg-rose-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900 sm:text-3xl">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const DetailItem: React.FC<{
  label: string;
  value: React.ReactNode;
  tone?: "slate" | "emerald" | "sky" | "amber" | "rose";
}> = ({ label, value, tone = "slate" }) => {
  const tones = {
    slate: "border-slate-200 bg-slate-100",
    emerald: "border-emerald-200 bg-emerald-50",
    sky: "border-sky-200 bg-sky-50",
    amber: "border-amber-200 bg-amber-50",
    rose: "border-rose-200 bg-rose-50",
  };

  return (
    <div className={`rounded-2xl border px-3 py-3 ${tones[tone]}`}>
      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
        {label}
      </p>

      <p className="mt-1 break-words text-[13px] font-black text-slate-900">
        {value || "—"}
      </p>
    </div>
  );
};

const NCBATransactionsPage: React.FC = () => {
  const { showToast } = useToast();

  const [transactions, setTransactions] = useState<NCBATransaction[]>([]);
  const [stats, setStats] = useState<NCBAStats>({
    total: 0,
    processed: 0,
    failed: 0,
    unprocessed: 0,
    processed_total: 0,
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);

  const [status, setStatus] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedTx, setSelectedTx] = useState<NCBATransaction | null>(null);
  const [showCorrectModal, setShowCorrectModal] = useState(false);
  const [showRawModal, setShowRawModal] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [correctBillRef, setCorrectBillRef] = useState("");

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(total / pageSize));
  }, [total, pageSize]);

  const loadTransactions = async (manual = false) => {
    try {
      if (manual) setRefreshing(true);
      else setLoading(true);

      setError(null);

      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("page_size", String(pageSize));

      if (status && status !== "all") {
        params.set("status", status);
      }

      if (search.trim()) {
        params.set("search", search.trim());
      }

      const res = await fetchApp<NCBATransactionsResponse>(
        `/payments/ncba/transactions?${params.toString()}`,
      );

      if (!res.ok) {
        throw new Error(res.message || "Failed to load NCBA transactions.");
      }

      setTransactions(Array.isArray(res.items) ? res.items : []);
      setStats(
        res.stats || {
          total: 0,
          processed: 0,
          failed: 0,
          unprocessed: 0,
          processed_total: 0,
        },
      );
      setTotal(Number(res.total || 0));
    } catch (err: any) {
      const msg = err?.message || "Failed to load NCBA transactions.";
      setError(msg);
      showToast(msg, "error");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadTransactions(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, status, search]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const clearFilters = () => {
    setSearch("");
    setSearchInput("");
    setStatus("all");
    setPage(1);
  };

  const openCorrectModal = (tx: NCBATransaction) => {
    setSelectedTx(tx);
    setCorrectBillRef(tx.bill_ref_number || "");
    setShowCorrectModal(true);
  };

  const openRawModal = (tx: NCBATransaction) => {
    setSelectedTx(tx);
    setShowRawModal(true);
  };

  const handleCorrectAndBill = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedTx) return;

    if (!correctBillRef.trim()) {
      showToast("Correct account number is required.", "error");
      return;
    }

    setShowConfirmDialog(true);
  };

  const confirmCorrectAndBill = async () => {
    if (!selectedTx) return;

    try {
      setWorking(true);

      await fetchApp(
        `/payments/ncba/transactions/${selectedTx.id}/correct-and-bill`,
        {
          method: "POST",
          body: JSON.stringify({
            bill_ref_number: correctBillRef.trim(),
          }),
        },
      );

      showToast("Transaction corrected and billed successfully.", "success");

      setShowConfirmDialog(false);
      setShowCorrectModal(false);
      setSelectedTx(null);
      setCorrectBillRef("");

      await loadTransactions(true);
    } catch (err: any) {
      showToast(err?.message || "Failed to correct transaction.", "error");
    } finally {
      setWorking(false);
    }
  };

  const hasFilters = Boolean(search || status !== "all");

  if (loading && transactions.length === 0) {
    return <LoadingState />;
  }

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  NCBA Paybill Payments
                </div>

                <h1 className="mt-3 text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                  NCBA Transactions
                </h1>

                <p className="mt-1 max-w-2xl text-[13px] font-medium text-slate-500 sm:text-[13.5px]">
                  Review NCBA paybill payments, track processed and failed
                  transactions, and correct wrong account numbers for billing.
                </p>
              </div>

              <button
                type="button"
                onClick={() => loadTransactions(true)}
                disabled={refreshing}
                className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
              >
                {refreshing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {refreshing ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>
        </div>

        {refreshing && (
          <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            Refreshing NCBA transactions…
          </div>
        )}

        {error && (
          <div className="flex items-start justify-between gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{error}</p>
            </div>

            <button
              type="button"
              onClick={() => setError(null)}
              className="rounded-xl p-1.5 text-rose-600 transition-colors hover:bg-rose-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5 xl:gap-4">
          <StatCard
            label="Total"
            value={stats.total}
            sub="All matching transactions"
            icon={<ReceiptText className="h-5 w-5" />}
            tone="slate"
          />

          <StatCard
            label="Processed"
            value={stats.processed}
            sub="Billed successfully"
            icon={<CheckCircle2 className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="Failed"
            value={stats.failed}
            sub="Needs attention"
            icon={<ShieldAlert className="h-5 w-5" />}
            tone="rose"
          />

          <StatCard
            label="Unprocessed"
            value={stats.unprocessed}
            sub="Not yet processed"
            icon={<Clock className="h-5 w-5" />}
            tone="amber"
          />

          <StatCard
            label="Current Page"
            value={transactions.length}
            sub={`Page ${page} of ${totalPages}`}
            icon={<Wallet className="h-5 w-5" />}
            tone="sky"
          />
        </div>

        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-[14px] font-black text-slate-900">
                Transaction List
              </p>

              <p className="text-[11.5px] font-medium text-slate-500">
                Showing {transactions.length} of {total} transactions
              </p>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:items-center">
              <form
                onSubmit={handleSearchSubmit}
                className="relative w-full lg:w-96"
              >
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search trans ID, phone, customer, account..."
                  className={`${inputCls} pl-10 pr-10`}
                />

                {searchInput && (
                  <button
                    type="button"
                    onClick={() => setSearchInput("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-xl p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </form>

              <div className="relative w-full lg:w-56">
                <Filter className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <select
                  value={status}
                  onChange={(e) => {
                    setPage(1);
                    setStatus(e.target.value as StatusFilter);
                  }}
                  className={`${inputCls} cursor-pointer appearance-none pl-10 pr-9`}
                >
                  <option value="all">All Status</option>
                  <option value="processed">Processed</option>
                  <option value="failed">Failed</option>
                  <option value="unprocessed">Unprocessed</option>
                </select>
              </div>

              <button
                type="button"
                onClick={() => {
                  setPage(1);
                  setSearch(searchInput.trim());
                }}
                className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
              >
                <Search className="h-4 w-4" />
                Search
              </button>

              {hasFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  <X className="h-4 w-4" />
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-[1250px] w-full text-left text-[13px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100 text-[11px] uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-3.5 font-black">Customer</th>
                  <th className="px-5 py-3.5 font-black">Transaction</th>
                  <th className="px-5 py-3.5 font-black">Account Ref</th>
                  <th className="px-5 py-3.5 font-black">Amount</th>
                  <th className="px-5 py-3.5 font-black">Status</th>
                  <th className="px-5 py-3.5 font-black">Error</th>
                  <th className="px-5 py-3.5 font-black">Received</th>
                  <th className="px-5 py-3.5 text-right font-black">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                {transactions.length > 0 ? (
                  transactions.map((tx) => (
                    <tr
                      key={tx.id}
                      className="transition-colors hover:bg-emerald-50/60"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                            <Smartphone className="h-4 w-4" />
                          </div>

                          <div className="min-w-0">
                            <p className="truncate text-[13px] font-black text-slate-900">
                              {tx.customer_name || "Unknown Customer"}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <p className="font-mono text-[12px] font-black text-slate-900">
                          {tx.trans_id || "—"}
                        </p>

                        <p className="mt-0.5 text-[11.5px] font-medium text-slate-500">
                          FT Ref: {tx.ft_ref || "—"}
                        </p>

                        <p className="mt-0.5 text-[11.5px] font-medium text-slate-500">
                          {tx.trans_type || "—"}
                        </p>
                      </td>

                      <td className="px-5 py-4">
                        <span className="inline-flex rounded-xl border border-sky-200 bg-sky-50 px-2.5 py-1 font-mono text-[12px] font-black text-sky-800">
                          {tx.bill_ref_number || "—"}
                        </span>

                        {tx.narrative && (
                          <p className="mt-1 max-w-[220px] truncate text-[11.5px] font-medium text-slate-500">
                            {tx.narrative}
                          </p>
                        )}
                      </td>

                      <td className="px-5 py-4 font-black text-slate-900">
                        {formatCurrencyFromCents(tx.trans_amount)}
                      </td>

                      <td className="px-5 py-4">
                        <StatusBadge status={tx.status} />
                      </td>

                      <td className="max-w-[280px] px-5 py-4">
                        {tx.process_error ? (
                          <p className="line-clamp-2 text-[12px] font-bold text-rose-700">
                            {tx.process_error}
                          </p>
                        ) : (
                          <span className="text-[12px] font-medium text-slate-400">
                            —
                          </span>
                        )}
                      </td>

                      <td className="px-5 py-4 text-[12px] font-medium text-slate-500">
                        {formatDateTime(tx.created_at)}
                      </td>

                      <td className="px-5 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openRawModal(tx)}
                            className={`${btnBase} h-9 border border-slate-300 bg-slate-100 px-3 text-slate-700 hover:bg-slate-200`}
                          >
                            <Eye className="h-3.5 w-3.5" />
                            Raw
                          </button>

                          <button
                            type="button"
                            onClick={() => openCorrectModal(tx)}
                            className={`${btnBase} h-9 border border-amber-200 bg-amber-100 px-3 text-amber-800 hover:bg-amber-200`}
                          >
                            <PencilLine className="h-3.5 w-3.5" />
                            Correct
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="px-5 py-16 text-center">
                      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                        <ReceiptText className="h-7 w-7 text-slate-500" />
                      </div>

                      <p className="text-[14px] font-black text-slate-800">
                        No NCBA transactions found
                      </p>

                      <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                        Transactions will appear here after NCBA sends payment
                        notifications.
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="divide-y divide-slate-200 bg-slate-50 md:hidden">
            {transactions.length > 0 ? (
              transactions.map((tx) => (
                <div key={tx.id} className="px-4 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-[14px] font-black text-slate-900">
                        {tx.customer_name || "Unknown Customer"}
                      </p>

                      <p className="mt-0.5 text-[12px] font-medium text-slate-500">
                        {tx.mobile || "No phone"}
                      </p>
                    </div>

                    <StatusBadge status={tx.status} />
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <DetailItem
                      label="Account Ref"
                      value={tx.bill_ref_number || "—"}
                      tone="sky"
                    />

                    <DetailItem
                      label="Amount"
                      value={formatCurrencyFromCents(tx.trans_amount)}
                      tone="emerald"
                    />

                    <DetailItem
                      label="Transaction"
                      value={tx.trans_id || "—"}
                    />

                    <DetailItem
                      label="Received"
                      value={formatDateTime(tx.created_at)}
                    />
                  </div>

                  {tx.process_error && (
                    <div className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-3 text-[12px] font-bold text-rose-700">
                      {tx.process_error}
                    </div>
                  )}

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => openRawModal(tx)}
                      className={`${btnBase} h-10 border border-slate-300 bg-slate-100 px-3 text-slate-700 hover:bg-slate-200`}
                    >
                      <Eye className="h-4 w-4" />
                      Raw
                    </button>

                    <button
                      type="button"
                      onClick={() => openCorrectModal(tx)}
                      className={`${btnBase} h-10 border border-amber-200 bg-amber-100 px-3 text-amber-800 hover:bg-amber-200`}
                    >
                      <PencilLine className="h-4 w-4" />
                      Correct
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-16 text-center">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                  <ReceiptText className="h-7 w-7 text-slate-500" />
                </div>

                <p className="text-[14px] font-black text-slate-800">
                  No NCBA transactions found
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3 border-t border-slate-200 bg-slate-100/80 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <p className="text-[12px] font-medium text-slate-500">
              Page <span className="font-black text-slate-800">{page}</span> of{" "}
              <span className="font-black text-slate-800">{totalPages}</span> •{" "}
              <span className="font-black text-slate-800">{total}</span>{" "}
              transactions
            </p>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <select
                value={pageSize}
                onChange={(e) => {
                  setPage(1);
                  setPageSize(Number(e.target.value));
                }}
                className={`${inputCls} w-full cursor-pointer sm:w-28`}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-700 hover:bg-slate-100`}
                >
                  Previous
                </button>

                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() =>
                    setPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-700 hover:bg-slate-100`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showCorrectModal && selectedTx && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-[2px]">
          <div className="w-full max-w-2xl overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-100/90 px-5 py-4">
              <div>
                <p className="text-[15px] font-black text-slate-900">
                  Correct Account Number & Bill
                </p>

                <p className="mt-1 text-[12.5px] font-medium text-slate-500">
                  Use this when the customer paid with the wrong account number.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowCorrectModal(false)}
                disabled={working}
                className="rounded-2xl p-2 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleCorrectAndBill} className="space-y-4 p-5">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <DetailItem
                  label="Customer"
                  value={selectedTx.customer_name || "Unknown Customer"}
                  tone="emerald"
                />

                <DetailItem
                  label="Phone"
                  value={selectedTx.mobile || "—"}
                  tone="sky"
                />

                <DetailItem
                  label="Amount"
                  value={formatCurrencyFromCents(selectedTx.trans_amount)}
                  tone="amber"
                />

                <DetailItem
                  label="Current Account Ref"
                  value={selectedTx.bill_ref_number || "—"}
                  tone="rose"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                  Correct Account Number / Bill Reference
                </label>

                <input
                  value={correctBillRef}
                  onChange={(e) => setCorrectBillRef(e.target.value)}
                  placeholder="Enter correct account number"
                  className={inputCls}
                />
              </div>

              <div className="rounded-3xl border border-amber-200 bg-amber-50 px-4 py-3 text-[12.5px] font-bold text-amber-800">
                This will update the transaction bill reference and attempt to
                bill the correct customer account.
              </div>

              <div className="flex flex-col-reverse gap-2 border-t border-slate-200 pt-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setShowCorrectModal(false)}
                  disabled={working}
                  className={`${btnBase} h-11 border border-slate-300 bg-slate-50 px-5 text-slate-700 hover:bg-slate-100`}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={working}
                  className={`${btnBase} h-11 bg-gradient-to-r from-emerald-500 to-teal-500 px-6 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                >
                  {working ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  {working ? "Processing…" : "Correct & Bill"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showRawModal && selectedTx && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-[2px]">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-100/90 px-5 py-4">
              <div>
                <p className="text-[15px] font-black text-slate-900">
                  Raw NCBA Request
                </p>

                <p className="mt-1 text-[12.5px] font-medium text-slate-500">
                  Transaction {selectedTx.trans_id || selectedTx.id}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowRawModal(false)}
                className="rounded-2xl p-2 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="max-h-[calc(90vh-90px)] overflow-auto p-5">
              <pre className="whitespace-pre-wrap break-words rounded-3xl border border-slate-200 bg-slate-950 p-4 text-[12px] font-medium leading-relaxed text-slate-100">
                {JSON.stringify(selectedTx.raw_request || selectedTx, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={showConfirmDialog}
        loading={working}
        variant="danger"
        title="Confirm Transaction Correction"
        confirmLabel="Correct & Bill"
        cancelLabel="Cancel"
        description={`You are about to correct this transaction bill reference from "${
          selectedTx?.bill_ref_number || "empty"
        }" to "${correctBillRef.trim()}" and bill the customer. This action will attempt to process the payment against the corrected account.`}
        onCancel={() => {
          if (!working) setShowConfirmDialog(false);
        }}
        onConfirm={confirmCorrectAndBill}
      />
    </div>
  );
};

export default NCBATransactionsPage;
